import { UseMutationResult } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { FieldValues, UseFormReturn } from "react-hook-form";

import { useTranslation } from "../../core/translation.core";
import LoadingOvalIcon from "../../icons/loadingOval.icon";
import { SubmitPanel } from "../reuseables/editor.comp";
import ExclamationTriangleIcon from "../../icons/exclamationTriangle.icon";

interface Props<T extends FieldValues> {
  methods: UseFormReturn<T, any>;
  mutation: UseMutationResult<void, Error, T, unknown>;
  btnText: string;
  children: React.ReactNode;
  cancelPath?: string;
}

function parseError(error: Error | null): string[] {
  if (!error) return [];

  const errorMessage = error.message || error.toString();

  // Try to parse JSON error response
  try {
    // Error message format: "Fetch Failed with url '/admin/...': {...}"
    const jsonMatch = errorMessage.match(/: ({.+})$/);
    if (jsonMatch) {
      const errorData = JSON.parse(jsonMatch[1]);

      // Handle 422 validation errors
      if (errorData.errors) {
        const errorMessages: string[] = [];
        Object.keys(errorData.errors).forEach((field) => {
          const fieldError = errorData.errors[field];
          if (fieldError && fieldError.msg) {
            errorMessages.push(`${field}: ${fieldError.msg}`);
          } else if (fieldError && typeof fieldError === "string") {
            errorMessages.push(`${field}: ${fieldError}`);
          }
        });
        if (errorMessages.length > 0) {
          return errorMessages;
        }
      }

      // Handle other error formats
      if (errorData.message) {
        return [errorData.message];
      }
    }
  } catch (e) {
    // If parsing fails, fall through to default handling
  }

  // Default: show the error message
  return [errorMessage];
}

export default function GenericFormEditor<T extends FieldValues>({ methods, mutation, btnText, children, cancelPath }: Props<T>) {
  const error = mutation.error;
  const errorMessages = parseError(error);
  const { push, back } = useRouter();
  const { t, msg } = useTranslation();

  async function onSubmit(data: T) {
    mutation.mutate(data);
  }

  function handleCancel() {
    if (cancelPath) {
      push(cancelPath);
    } else {
      back();
    }
  }

  return (
    <form onSubmit={methods.handleSubmit(onSubmit)} className="pb-20">
      {errorMessages.length > 0 && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
          <p className="text-red-600 dark:text-red-400 font-semibold mb-2">Error:</p>
          <ul className="list-disc list-inside space-y-1">
            {errorMessages.map((msg, idx) => (
              <li key={idx} className="text-red-600 dark:text-red-400 text-sm">
                {msg}
              </li>
            ))}
          </ul>
        </div>
      )}
      {children}

      <SubmitPanel>
        <button data-action="cancel" type="button" onClick={handleCancel}>
          {t(msg.form.cancel)}
        </button>
        {errorMessages.length > 0 && (
          <div className="text-red-600 dark:text-red-400">
            <ExclamationTriangleIcon className="w-6 h-6 text-red-400" />
          </div>
        )}
        <input type="submit" value={btnText} disabled={mutation.isPending} />
        {mutation.isPending && <LoadingOvalIcon />}
      </SubmitPanel>
    </form>
  );
}
